import React, { useMemo } from 'react';
import {
  ReportColumn,
  DEFAULT_SHOW,
  ACTION_COLUMN,
  ReportFilter,
} from 'components/modules/report/report-types';
import { Enum, Model } from 'util/backendapi/models/api.interfaces';
import { Trans, t } from '@lingui/macro';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { ReportTable } from 'components/modules/report/table/ReportTable';
import { ReportFiltersBlock } from 'components/modules/report/filter/ReportFiltersBlock';
import { ReportStateProps } from 'hooks/use-report-state';
import ActionBlock from 'components/base/actionblock/actionblock';
import { reportFilterRouterParam } from 'components/modules/report/filter/fields/FilterRouterParam';
import ButtonShowModal, {
  ModalContentProps,
} from 'components/base/modal/buttonshowmodal';
import { HasPermission } from 'components/logic/has-permission/HasPermission';
import { EditObsPointRouteMarchInfoModal } from './EditObsPointRouteMarchInfoModal';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';
import { ButtonShowCommentsPanel } from 'components/modules/comments-panel/ButtonShowCommentsPanel';
import { RouteMarch, RouteMarchCopy } from 'util/backendapi/types/Model';
import Loading from 'components/base/loading/loading';
import { AlertDanger } from 'components/base/alert/alert';
import { CopyRouteMarchPanel } from './CopyRouteMarchPanel';
import { AddRouteMarchObsPointPanel } from './AddRouteMarchObsPointPanel';
import { useHistory } from 'react-router';
import { ExportReportButton } from 'components/modules/report/actions/ExportReportButton';
import { BackButton } from 'components/base/back-button/BackButton';

export const RouteMarchObsPointListFilters: ReportFilter[] = [
  reportFilterRouterParam(
    'route_march__code',
    <Trans>Route march</Trans>,
    'routeMarchCode'
  ),
];

export const RouteMarchObsPointListColumns: (props: {
  refreshList: () => void;
  removeObsPoint: (id: number) => Promise<void>;
}) => ReportColumn<Model.ReportsRouteMarchObservationPoint>[] = ({
  refreshList,
  removeObsPoint,
}) => [
  {
    label: <Trans>Observation point</Trans>,
    name: 'observation_point__code',
    visibility: DEFAULT_SHOW,
    accessor: ({ observation_point__code: code }) => code,
  },
  {
    label: <Trans>Civil feature</Trans>,
    name: 'civil_feature__name',
    visibility: DEFAULT_SHOW,
  },
  {
    label: <Trans>Instrument type</Trans>,
    name: 'instrument_type__code',
    additionalFields: ['instrument_type__name'],
    visibility: DEFAULT_SHOW,
    accessor: ({ instrument_type__code: code, instrument_type__name: name }) =>
      `${code} - ${name}`,
  },
  {
    label: <Trans>Device alarm value</Trans>,
    name: 'device_alarm_threshold',
    visibility: DEFAULT_SHOW,
    accessor: ({ device_alarm_threshold }) => device_alarm_threshold,
  },
  {
    label: <Trans>Route march instruction</Trans>,
    name: 'route_march_instruction',
    visibility: DEFAULT_SHOW,
    accessor: ({ route_march_instruction }) => route_march_instruction,
  },
  {
    label: <Trans>Emergency route march instruction</Trans>,
    name: 'emergency_route_march_instruction',
    visibility: DEFAULT_SHOW,
    accessor: ({ emergency_route_march_instruction }) =>
      emergency_route_march_instruction,
  },
  {
    label: <Trans>Input labels</Trans>,
    name: 'input_labels',
    visibility: DEFAULT_SHOW,
    accessor: ({ input_labels }) =>
      input_labels.map((label, idx) => (
        <React.Fragment key={idx}>
          {label}
          <br />
        </React.Fragment>
      )),
  },
  {
    ...ACTION_COLUMN,
    // NOTE: position must be included here for re-ordering to work
    additionalFields: [
      'id',
      'observation_point',
      'observation_point__code',
      'position',
    ],
    accessor: ({ id, observation_point, observation_point__code: code }) => (
      <HasPermission check="can_create_route_marches">
        <ButtonShowModal
          name={`edit-rmop-${id}`}
          iconOnly={true}
          iconType="icon-edit"
          className="btn-link-panel"
          title={t`Edit route march observation point`}
          modalContent={({ hideModal }: ModalContentProps) => (
            <EditObsPointRouteMarchInfoModal
              observationPointId={observation_point}
              onAfterSubmit={() => {
                hideModal();
                refreshList();
              }}
            />
          )}
        >
          <Trans>Edit</Trans>
        </ButtonShowModal>

        <ButtonShowConfirmation
          name={`remove-rmop-${id}`}
          title={t`Delete`}
          iconType="icon-circle-minus"
          className="btn-link-panel"
          content={
            <Trans>
              Are you sure you want to remove <strong>{code}</strong> from this
              route march? This action is not reversible.
            </Trans>
          }
          okBtnText={<Trans>Yes, remove</Trans>}
          onConfirm={async function () {
            await removeObsPoint(id);
            refreshList();
          }}
        />
      </HasPermission>
    ),
  },
];

export interface RouteMarchObsPointListViewProps {
  reportProps: ReportStateProps<Model.ReportsRouteMarchObservationPoint>;
  routeMarch: RouteMarch | null;
  removeObsPoint: (id: number) => Promise<void>;
  copyRouteMarch: (id: number, values: RouteMarchCopy) => Promise<RouteMarch>;
  createRouteMarchObsPoint: (
    routeMarchId: number,
    observationPointId: number,
    position: number
  ) => Promise<void>;
  onRecordDragged?: (sourceIndex: number, destIndex: number) => Promise<void>;
  isLoading: boolean;
  errorMessage: string;
}

export function RouteMarchObsPointListView(
  props: RouteMarchObsPointListViewProps
) {
  const {
    reportProps,
    routeMarch,
    removeObsPoint,
    copyRouteMarch,
    createRouteMarchObsPoint,
    onRecordDragged,
    isLoading,
    errorMessage,
  } = props;

  const columns = useMemo(
    () =>
      RouteMarchObsPointListColumns({
        refreshList: reportProps.refreshList,
        removeObsPoint,
      }),
    [reportProps.refreshList, removeObsPoint]
  );

  const filters = RouteMarchObsPointListFilters;

  const history = useHistory();

  let pageContent: React.ReactNode;
  if (routeMarch && !isLoading && !errorMessage) {
    pageContent = (
      <>
        <div className="page-content-header-with-back-button-wrapper page-content-header-filters-actions">
          {!reportProps.isExportMode && (
            <BackButton defaultBackUrl="/route-march/" />
          )}
          <ActionBlock className="text-right">
            <HasPermission check="can_create_route_march_comments">
              <ButtonShowCommentsPanel
                type={Enum.Comment_RESOURCE_TYPE.routeMarch}
                metadata={{
                  route_march: routeMarch.id,
                }}
                commentReportParams={`resourcetype=${Enum.Comment_RESOURCE_TYPE.routeMarch}&route_march=${routeMarch.id}`}
              />
            </HasPermission>
            <HasPermission check="can_create_route_marches">
              <ButtonShowModal
                name="copyRouteMarch"
                title={t`Copy route march`}
                modalContent={({ hideModal }: any) => (
                  <CopyRouteMarchPanel
                    hideModal={hideModal}
                    onSubmit={async (values) => {
                      const newRouteMarch = await copyRouteMarch(
                        routeMarch.id,
                        values
                      );
                      hideModal();
                      history.push(
                        `/route-march-observation-points/${newRouteMarch.code}`
                      );
                    }}
                  />
                )}
              >
                <Trans>Copy route march</Trans>
              </ButtonShowModal>
              <ButtonShowModal
                shortcut={'CREATE_NEW'}
                name="addObservationPoint"
                iconType="icon-plus"
                title={t`Add observation point`}
                modalContent={({ hideModal }: any) => (
                  <AddRouteMarchObsPointPanel
                    routeMarch={routeMarch}
                    routeMarchObservationPoints={reportProps.records}
                    createRouteMarchObsPoint={createRouteMarchObsPoint}
                    hideModal={hideModal}
                  />
                )}
              >
                <Trans>Add observation point</Trans>
              </ButtonShowModal>
            </HasPermission>
          </ActionBlock>

          <ReportFiltersBlock
            filtersFrontend={[]}
            filtersBackend={
              reportProps.reportInfo && reportProps.reportInfo.filters
            }
            isExportMode={reportProps.isExportMode}
          />
        </div>
        <div className="filtered-table-wrapper">
          <ActionBlock className="text-right">
            <ExportReportButton
              fileNameBase={t`Route march observation points report`}
              reportUrl="/reports/route-march-observation-points/"
              columns={columns}
              filters={filters}
              pdfExtraParams={{
                routeMarch: routeMarch,
              }}
            />
          </ActionBlock>

          <ReportTable
            columns={columns}
            errorMessage={reportProps.errorMessage}
            isLoading={reportProps.isLoading}
            msgNoMatches={
              <Trans>No route march observation points found.</Trans>
            }
            pagination={reportProps.pagination}
            records={reportProps.records}
            reportInfo={reportProps.reportInfo}
            onRecordDragged={onRecordDragged}
            draggableRowId={'id'}
          />
        </div>
      </>
    );
  } else if (errorMessage) {
    pageContent = <AlertDanger>{errorMessage}</AlertDanger>;
  } else {
    pageContent = <Loading />;
  }

  return (
    <PageStandard
      name="RouteMarchObsPoint-list"
      header={
        reportProps.isExportMode ? (
          <Trans>Route march</Trans>
        ) : (
          <Trans>Maintain route march</Trans>
        )
      }
      subHeader={routeMarch?.code}
    >
      {pageContent}
    </PageStandard>
  );
}
